import { HotelInfoModel, AvailableRoomType, AvailableRoomPlan } from '@/__generated';
import BoardTypeList from '@/components/App/Hotels/BoardTypeList';
import SingleOrPluralText from '@/components/App/SingleOrPluralText';
import { useCurrencyFormat } from '@/hooks/useCurrencyFormat';
import { min, minBy, range, sortBy } from 'lodash';
import React from 'react';
import Select from 'react-select';
import { Swiper, SwiperSlide } from 'swiper/react';
import { PopoverBody, PopoverHeader, UncontrolledPopover, UncontrolledTooltip } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faCircleExclamation, faCircleInfo, faCode } from '@fortawesome/free-solid-svg-icons';
import { faBuilding, faEye } from '@fortawesome/free-regular-svg-icons';
import { motion } from 'framer-motion';
import DefaultCancellationPolicy from './DefaultCancellationPolicy';
import { useLocation } from 'react-router-dom';
import { getHotelDetailRequest } from '@/urls';
import PlanTaxAndFee from './PlanTaxAndFee';
import { useGetQuery } from '@/hooks/useQuery';
import { ISelectedRoomTypes } from '../type';
import { HOTEL_CONFIG, HotelDisplayConfig } from '@/config';
import ImageGallery from 'react-image-gallery';
import RoomPlanMealDetail from '@/components/App/RoomPlanMealDetail';

type Props = {
  noOfAdult: number;
  noOfChild: number;
  hotelInfo: HotelInfoModel;
  availableRoomType: AvailableRoomType;
  selectedRooms: ISelectedRoomTypes[];
  onSelectedRoomChange: (
    noOfAdult: number,
    noOfChild: number,
    roomType: AvailableRoomType,
    plan: AvailableRoomPlan,
    selectedRoomNumber: number,
  ) => void;
};
const DISPLAY_PLAN_LIMIT = 3;

export default function RoomDetail({ availableRoomType, onSelectedRoomChange, noOfAdult, noOfChild, selectedRooms, hotelInfo }: Props) {
  const { formatCurrency } = useCurrencyFormat();
  const [showAllPlans, setShowAllPlans] = React.useState(false);
  const { search } = useLocation();
  const checkinDate = useGetQuery('checkinDate');
  const roomCount = React.useMemo(() => {
    let request = getHotelDetailRequest(search);
    if (request) {
      let rooms = request.rooms;
      return rooms.filter(x => x.noOfAdult === noOfAdult && x.noOfChild === noOfChild).length || 0;
    }
    return 0;
  }, [search, noOfAdult, noOfChild]);
  React.useEffect(() => {
    setShowAllPlans(false);
  }, [availableRoomType]);
  const room = React.useMemo(() => {
    return availableRoomType.roomInfo;
  }, [availableRoomType]);

  const roomPlans = React.useMemo(() => {
    let plans = sortBy(availableRoomType.roomPlans, x => x.price) || [];
    return showAllPlans ? plans : plans.filter((_, i) => i < DISPLAY_PLAN_LIMIT);
  }, [availableRoomType, showAllPlans]);

  const guestTooltipRef = React.useRef<any>();
  const roomFacilitiesRef = React.useRef<any>();

  const selectOptions = React.useMemo(() => {
    let noRooms = min([roomCount, availableRoomType.availableSlot || 0]) || 0;
    return range(1, noRooms + 1).map(c => ({
      label: c === 1 ? '1 room' : `${c} rooms`,
      value: c,
    }));
  }, [roomCount, availableRoomType]);

  const selectedRoom = React.useMemo(() => {
    let roomTypes = selectedRooms.find(x => x.noOfAdult === noOfAdult && x.noOfChild === noOfChild)?.roomTypes;
    if (roomTypes?.length) {
      return (
        roomTypes.find(
          x =>
            `${x.supplier}-${x.category}-${x.roomTypeCode}-${x.roomTypeId}` ===
            `${availableRoomType.supplier}-${availableRoomType.category}-${availableRoomType.roomTypeCode}-${availableRoomType.roomTypeId}`,
        )?.plans || []
      );
    }
    return null;
  }, [selectedRooms, noOfAdult, noOfChild, availableRoomType]);

  const roomImages = React.useMemo(() => {
    return room?.images?.map(x => ({ thumbnail: x.url || '', original: x.url || '', originalClass: 'w-100 col-12 rounded-4' })) || [];
  }, [room]);
  return (
    <div className={` px-30 py-30 sm:px-20 sm:py-20 border-bottom-light`} id={`${noOfAdult}-${noOfChild}-room-${availableRoomType.roomTypeId}`}>
      <div className="row y-gap-30">
        <div className="col-xl-3 col-xxl-2 col-md-12 ">
          <div className="pb-10">
            <div className="fw-500 text-18">[{availableRoomType.category}]</div>
            {room?.maxGuest || room?.roomTypeView ? (
              <div className="text-14 fw-500 xs:d-none md:d-none  text-blue-1 link-with-hover">&nbsp;</div>
            ) : null}
          </div>
          <div className=" col-12 col-md-4 col-xl-12 mt-10 ">
            {roomImages.length ? (
              <ImageGallery items={roomImages} lazyLoad showThumbnails={false} />
            ) : (
              <img className="w-100 col-12 rounded-4" src={HOTEL_CONFIG.defaultImage} alt={availableRoomType.category} />
            )}
          </div>
          {/* image */}

          <div>
            <div className="y-gap-5 mt-20">
              {room?.basicSize ? (
                <div className="d-flex items-center">
                  <i className="ficon ficon-sqm  text-20 mr-10" />
                  <div className="text-15">{`Room size: ${room?.basicSize}${room?.basicUnit}`}</div>
                </div>
              ) : null}

              {room?.noOfBeds ? (
                <div className="d-flex items-center">
                  <i className="ficon ficon-bed text-20 mr-10" />
                  <div className="text-15">
                    <SingleOrPluralText
                      count={room?.noOfBeds}
                      singleText={`{} ${room?.bedName || 'bed'}`}
                      pluralText={`{} ${room?.bedName || 'beds'}`}
                      afterText={room?.bedWidth && room?.bedLength ? ` ${room?.bedWidth}x${room?.bedLength}cm` : ''}
                    />
                  </div>
                </div>
              ) : null}
              {room ? (
                <>
                  <button className="d-block text-14 fw-500 text-blue-1" ref={roomFacilitiesRef}>
                    <i className="mr-10 ficon ficon-plus-with-circle text-20" />
                    See all room facilities
                  </button>
                  <UncontrolledPopover trigger="focus" target={roomFacilitiesRef} placement="top">
                    <PopoverHeader className="bg-blue-1 text-white">Room facilities</PopoverHeader>
                    <PopoverBody className="px-20 py-10 y-gap-10">
                      {room?.noOfExtraBeds ? (
                        <div className="d-flex items-center">
                          <i className="ficon ficon-extra-bed text-20 mr-10" />
                          <div className="text-15">
                            <SingleOrPluralText
                              count={room?.noOfExtraBeds}
                              singleText={`{} extra bed`}
                              pluralText={`{} extra beds`}
                              afterText={
                                room?.extraBedWidth && room?.extraBedLength ? ` ${room?.extraBedWidth}x${room?.extraBedLength}cm` : undefined
                              }
                            />
                          </div>
                        </div>
                      ) : null}

                      {room?.airConditioner ? (
                        <div className="d-flex items-center">
                          <i className="ficon ficon-air-conditioning text-20 mr-10" />
                          <div className="text-15">Air conditioner</div>
                        </div>
                      ) : null}

                      {room?.internetAvailable ? (
                        <div className="d-flex items-center">
                          <i className="ficon ficon-wired-internet text-20 mr-10" />
                          <div className="text-15">{`Internet [${room?.internetFee ? 'With charge' : 'Free'}]`}</div>
                        </div>
                      ) : null}

                      {room?.hasBath ? (
                        <div className="d-flex items-center">
                          <i className="ficon ficon-shared-bath text-20 mr-10" />
                          <div className="text-15">Bath room</div>
                        </div>
                      ) : null}

                      {room?.hasToilet ? (
                        <div className="d-flex items-center">
                          <i className="ficon ficon-regular-toilet text-20 mr-10" />
                          <div className="text-15">Toilet</div>
                        </div>
                      ) : null}

                      {room?.toiletShower ? (
                        <div className="d-flex items-center">
                          <i className="ficon ficon-confirmation-instant text-20 mr-10" />
                          <div className="text-15">Bidet toilet</div>
                        </div>
                      ) : null}

                      {room?.standWashing ? (
                        <div className="d-flex items-center">
                          <i className="ficon ficon-confirmation-instant text-20 mr-10" />
                          <div className="text-15">Stand washing</div>
                        </div>
                      ) : null}
                      {room?.openAirBath ? (
                        <div className="d-flex items-center">
                          <i className="ficon ficon-confirmation-instant text-20 mr-10" />
                          <div className="text-15">Open air bath</div>
                        </div>
                      ) : null}
                      {room?.showerBooth ? (
                        <div className="d-flex items-center">
                          <i className="ficon ficon-regular-shower-booth text-20 mr-10" />
                          <div className="text-15">Shower booth</div>
                        </div>
                      ) : null}
                      {room?.spaBath ? (
                        <div className="d-flex items-center">
                          <i className="ficon ficon-regular-spa-sauna text-20 mr-10" />
                          <div className="text-15">Spa sauna</div>
                        </div>
                      ) : null}
                      {room?.jacuzzi ? (
                        <div className="d-flex items-center">
                          <i className="ficon ficon-jacuzzi-bathtub text-20 mr-10" />
                          <div className="text-15">Jacuzzi</div>
                        </div>
                      ) : null}
                      {room?.washingArea ? (
                        <div className="d-flex items-center">
                          <i className="ficon ficon-wooden-parqueted-flooring text-20 mr-10" />
                          <div className="text-15">Washing area</div>
                        </div>
                      ) : null}
                    </PopoverBody>
                  </UncontrolledPopover>
                </>
              ) : null}
            </div>
          </div>
        </div>

        <div className="col-xl-9 col-xxl-10 col-md-12">
          <div className="row ">
            <div className="pb-10 col-md-12 col-xs-12 col-xl">
              <div className="fw-500 text-18">{availableRoomType.name}</div>
              <div className="d-flex items-center x-gap-30">
                {room?.maxGuest ? (
                  <span className="d-flex items-center ">
                    <svg width="16" height="16" fill="none">
                      <path
                        d="M2 14v-1.333A2.667 2.667 0 014.667 10h2.666A2.667 2.667 0 0110 12.667V14m.667-11.913a2.667 2.667 0 010 5.166M14 14v-1.333a2.667 2.667 0 00-2-2.567M8.667 4.667a2.667 2.667 0 11-5.334 0 2.667 2.667 0 015.334 0z"
                        stroke="#4A5568"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>
                    </svg>
                    <span className="fw-500 text-14 px-5">
                      <SingleOrPluralText count={room?.maxGuest || 0} singleText="{} guest" pluralText="{} guests" />
                    </span>
                    <button className="text-14 fw-500 underline text-blue-1 link-with-hover" ref={guestTooltipRef}>
                      (More detail)
                    </button>
                    <UncontrolledTooltip target={guestTooltipRef}>
                      <div className="px-10 py-10">
                        <ul className="list-disc y-gap-4 pt-5 text-left">
                          <li>
                            Maximum: <SingleOrPluralText count={room?.maxGuest || 0} singleText="{} guest" pluralText="{} guests" />
                          </li>
                          <li>
                            Minimum: <SingleOrPluralText count={room?.minGuest || 0} singleText="{} guest" pluralText="{} guests" />
                          </li>
                        </ul>
                      </div>
                    </UncontrolledTooltip>
                  </span>
                ) : null}

                {room?.roomTypeView ? (
                  <span className="fw-500 text-14">
                    <FontAwesomeIcon icon={faEye} className="mr-5 " style={{ color: '#4A5568' }} />
                    {room?.roomTypeView}
                  </span>
                ) : null}
                {room?.buildingName ? (
                  <span className="fw-500 text-14">
                    <FontAwesomeIcon icon={faBuilding} className="mr-5 " style={{ color: '#4A5568' }} />
                    {`Building: ${room?.buildingName}`}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="col-auto">
              <div className="px-10 bg-error-1 rounded">
                <span className="text-14 text-red-1 fw-500 text-14">
                  <FontAwesomeIcon icon={faCircleExclamation} className="mr-5" />
                  <SingleOrPluralText count={availableRoomType.availableSlot || 0} singleText="Only {} room left" pluralText="Only {} rooms left" />
                </span>
              </div>
            </div>
          </div>
          {roomPlans.map(plan => {
            const count = selectedRoom?.find(x => x.roomPlanId === plan.planId)?.selectedRooms;
            const defaultSelected = count ? selectOptions.find(x => x.value === count) : undefined;
            const bgColor = HotelDisplayConfig.RoomColor.__getRoomColor(availableRoomType.supplier);
            return (
              <motion.div
                className={` rounded-4 px-30 py-20 mt-10`}
                key={plan.code}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{ backgroundColor: bgColor }}
                transition={{
                  duration: 1,
                  delay: 0.1,
                  ease: [0, 0.71, 0.2, 1.01],
                }}
              >
                <div className="row y-gap-30">
                  <div className="col-lg col-md-6">
                    <div className="text-15 fw-500 mb-10">{`${plan.name || ''} ${plan.code || ''}`}</div>
                    <div className="y-gap-5">
                      <div className="d-flex items-center text-green-2">
                        <i className="icon-check text-12 mr-10" />
                        <div className="text-15">
                          <BoardTypeList
                            boardTypes={[plan.boardType || '']}
                            additionalIcon={<RoomPlanMealDetail planDetail={plan} supplier={availableRoomType.supplier} />}
                          />
                        </div>
                      </div>
                      {plan.planTitle ? (
                        <div className="d-flex items-center text-green-2">
                          <i className="icon-check text-12 mr-10" />
                          <div className="text-14 fw-500">
                            {plan.planTitle}
                            {plan.planDesc ? (
                              <span>
                                <FontAwesomeIcon icon={faCircleInfo} className="text-blue-1 font-16 ml-5" id={`room-planDesc-${plan.code}`} />
                                <UncontrolledTooltip target={`room-planDesc-${plan.code}`}>{plan.planDesc}</UncontrolledTooltip>
                              </span>
                            ) : null}
                          </div>
                        </div>
                      ) : null}

                      <div className="d-flex items-center text-green-2">
                        <i className="icon-check text-12 mr-10" />
                        <div className="text-14 fw-500">
                          {[
                            `Check-in time: ${plan.checkinTime || hotelInfo.checkinTime}`,
                            `Check-out time: ${plan.checkoutTime || hotelInfo.checkoutTime}`,
                          ].join(', ')}
                        </div>
                      </div>

                      <DefaultCancellationPolicy plan={plan} checkinDate={checkinDate} />
                    </div>
                  </div>

                  <div className="col-lg-3  col-md-6 border-left-light lg:border-none">
                    <div className="px-10 lg:px-0">
                      <div className="text-15 fw-500 mb-10">Select Rooms</div>
                      <Select
                        onChange={(selectedValue: any) => {
                          onSelectedRoomChange(noOfAdult, noOfChild, availableRoomType, plan, selectedValue?.value || 0);
                        }}
                        isClearable
                        placeholder="Not selected"
                        options={selectOptions as any}
                        value={defaultSelected}
                        className="rounded-4 text-14 w-100 fw-500"
                      />
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6 border-left-light lg:border-none text-right lg:text-left">
                    <div className="pl-40 lg:pl-0">
                      <div className="text-15 fw-500 mb-10">Price for 1 room</div>
                      <div className=" lh-1">
                        <div className="text-22 fw-500 mb-5">
                          {formatCurrency(plan.price)}
                          {plan.paymentNotes ? (
                            <>
                              <FontAwesomeIcon icon={faCircleInfo} className="text-blue-1 font-16 ml-5" id={`room-paymentNotes-${plan.code}`} />
                              <UncontrolledTooltip target={`room-paymentNotes-${plan.code}`}>{plan.paymentNotes}</UncontrolledTooltip>
                            </>
                          ) : null}
                        </div>
                        <PlanTaxAndFee plan={plan} className="text-14  text-blue-1 fw-500" />

                        {plan.remarks ? (
                          <>
                            {availableRoomType.supplier !== 'NTA' ? (
                              <div className="text-14 text-red-1 mt-5">Additional charges may apply upon check in</div>
                            ) : (
                              <div className="mt-5">
                                <div className="text-14 text-red-1 fw-500">
                                  Infant remarks
                                  <FontAwesomeIcon icon={faCircleInfo} className="text-red-1 font-16 ml-5" id={`room-infantRemarks-${plan.code}`} />
                                </div>

                                <UncontrolledTooltip target={`room-infantRemarks-${plan.code}`}>{plan.remarks}</UncontrolledTooltip>
                              </div>
                            )}
                          </>
                        ) : null}

                        <div className="text-14 text-light-1 mt-5">{`Supplier ${availableRoomType.supplier}`}</div>
                      </div>
                    </div>
                  </div>
                  {/* End .col */}
                </div>
              </motion.div>
            );
          })}
          {(availableRoomType.roomPlans?.length || 0) > DISPLAY_PLAN_LIMIT ? (
            <button
              onClick={() => setShowAllPlans(p => !p)}
              className="bg-white rounded-4 px-10 py-5 mt-10 d-flex items-center justify-center text-blue-1 text-14 w-100"
            >
              <FontAwesomeIcon icon={showAllPlans ? faChevronUp : faChevronDown} className="mr-5" />
              {showAllPlans ? 'Collapse' : `Show more`}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
